import { Link } from 'react-router-dom';
import {
  FaHome,
  FaHeart,
  FaVideo,
  FaPlus,
  FaUserCircle,
  FaUsers,
  FaBroadcastTower
} from 'react-icons/fa';
import { MdLock, MdLockOpen, MdLocalHospital } from 'react-icons/md';
import UploadModal from '../UploadModal';
import { User } from '../../types/entities';
import useSidebar from './useSidebar';
import { DEMO_MODE } from '../../config';
import { useOriginAccess } from '../../hooks/useOriginAccess';
import styles from './Sidebar.module.css';

interface MobileFooterProps {
  currentUser?: User;
  className?: string;
  onOpenUploadModal?: () => void;
}

/**
 * SP用フッターナビゲーションコンポーネント
 * 
 * @param props - MobileFooterコンポーネントのプロパティ
 * @returns MobileFooterコンポーネント
 */
const MobileFooter = ({ currentUser, className, onOpenUploadModal }: MobileFooterProps) => {
  const {
    isModalOpen,
    isDropdownOpen,
    openModal,
    closeModal,
    toggleDropdown,
    handleAccount,
    handleLogout,
    handleOverlayClick,
    handleUserManagementMobile,
    handleAdminNotificationsMobile,
    isAdmin,
    iconUrl,
    userName,
    dropdownRef,
    menuRef,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    swiping,
    isDraggingHandle,
    selectedMyVideoStatus,
    toggleMyVideoDropdown,
    isMyVideoSubmenuOpen,
    myVideoSubmenuRef,
    closeMyVideoSubmenu
  } = useSidebar(currentUser);
  const handleOpenUploadModal = onOpenUploadModal ?? openModal;
  // 院外接続ではアップロードを backend が拒否するため、入口（+ボタン）を非表示にする。
  const { isExternal } = useOriginAccess();

  return (
    <>
      {/* スマホ・タブレット用：下部フッターナビゲーション */}
      <div className={styles['mobile-footer-navigation']}>
        <nav className={styles['navigation-footer']}>
          <ul className={styles['menu-footer']}>
            <li className={styles['menu-item-footer']}>
              <Link to="/" className={styles['menu-link-footer']}>
                <FaHome className={styles['icon-footer']} />
                <span className={styles['label-footer']}>ホーム</span>
              </Link>
            </li>
            <li className={styles['menu-item-footer']}>
              <Link to="/live-streams" className={styles['menu-link-footer']}>
                <FaBroadcastTower className={styles['icon-footer']} />
                <span className={styles['label-footer']}>ライブ配信</span>
              </Link>
            </li>
            <li className={styles['menu-item-footer']}>
              <Link to="/my-favorites" className={styles['menu-link-footer']}>
                <FaHeart className={styles['icon-footer']} />
                <span className={styles['label-footer']}>お気に入り</span>
              </Link>
            </li>
            <li className={styles['menu-item-footer']} style={{ position: 'relative' }}>
              <button
                onClick={toggleMyVideoDropdown}
                className={styles['menu-link-footer']}
              >
                <FaVideo className={styles['icon-footer']} />
                <span className={styles['label-footer']}>マイビデオ</span>
              </button>
              {/* モバイル版ではクリックで開閉（マイビデオアイコンの上に表示） */}
              {isMyVideoSubmenuOpen && (
                <div
                  ref={myVideoSubmenuRef}
                  className={styles['submenu-mobile']}
                >
                  <Link
                    to="/my-videos?status=public"
                    className={`${styles['submenu-link-mobile']} ${selectedMyVideoStatus === 'public' ? styles['submenu-link-active-mobile'] : ''}`}
                    onClick={closeMyVideoSubmenu}
                  >
                    <MdLockOpen className={`${styles['submenu-icon-mobile']} ${selectedMyVideoStatus === 'public' ? styles['submenu-icon-active-mobile'] : ''}`} />
                    <span className={selectedMyVideoStatus === 'public' ? styles['submenu-label-active-mobile'] : ''}>公開中</span>
                  </Link>
                  <Link
                    to="/my-videos?status=hospital_only"
                    className={`${styles['submenu-link-mobile']} ${selectedMyVideoStatus === 'hospital_only' ? styles['submenu-link-active-mobile'] : ''}`}
                    onClick={closeMyVideoSubmenu}
                  >
                    <MdLocalHospital className={`${styles['submenu-icon-mobile']} ${selectedMyVideoStatus === 'hospital_only' ? styles['submenu-icon-active-mobile'] : ''}`} />
                    <span className={selectedMyVideoStatus === 'hospital_only' ? styles['submenu-label-active-mobile'] : ''}>院内限定公開</span>
                  </Link>
                  <Link
                    to="/my-videos?status=private"
                    className={`${styles['submenu-link-mobile']} ${selectedMyVideoStatus === 'private' ? styles['submenu-link-active-mobile'] : ''}`}
                    onClick={closeMyVideoSubmenu}
                  >
                    <MdLock className={`${styles['submenu-icon-mobile']} ${selectedMyVideoStatus === 'private' ? styles['submenu-icon-active-mobile'] : ''}`} />
                    <span className={selectedMyVideoStatus === 'private' ? styles['submenu-label-active-mobile'] : ''}>非公開</span>
                  </Link>
                </div>
              )}
            </li>
            {/* アカウントボタンを一番右に配置 */}
            <li className={styles['menu-item-footer']} ref={dropdownRef}>
              <button
                className={styles['menu-link-footer']}
                onClick={toggleDropdown}
                aria-label="アカウントメニュー"
              >
                {iconUrl ? (
                  <img
                    src={iconUrl}
                    alt="User Icon"
                    className={styles['account-icon-footer']}
                  />
                ) : (
                  <FaUserCircle className={styles['icon-footer']} />
                )}
                <span className={styles['label-footer']}>アカウント</span>
              </button>
            </li>
          </ul>
        </nav>
      </div>

      {/* 動画投稿ボタン：フッターのすぐ上・画面右下に配置。デモモード or 院外接続では非表示。 */}
      {!DEMO_MODE && !isExternal && (
        <button
          className={styles['upload-fab']}
          onClick={handleOpenUploadModal}
          aria-label="動画をアップロード"
        >
          <FaPlus className={styles['upload-fab-icon']} />
        </button>
      )}

      {/* モバイル用オーバーレイ背景 */}
      {isDropdownOpen && (
        <div
          className={styles['menu-overlay']}
          onClick={handleOverlayClick}
          data-testid="sidebar__menu-overlay"
        />
      )}

      {/* モバイル用ドロップダウンメニュー - 画面下から表示 */}
      {isDropdownOpen && (
        <div
          ref={menuRef}
          className={`${styles['dropdown-menu-mobile']} ${swiping ? styles['swiping'] : ''} ${isDraggingHandle ? styles['handle-dragging'] : ''}`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          data-testid="sidebar__dropdown-menu-mobile"
        >
          {/* ユーザー情報セクション */}
          <div style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {iconUrl ? (
                <img
                  src={iconUrl}
                  alt="User Icon"
                  style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                />
              ) : (
                <FaUserCircle style={{ fontSize: '40px', color: '#666' }} />
              )}
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{userName}</div>
                <div style={{ fontSize: '14px', color: '#666' }}>{currentUser?.email || ''}</div>
              </div>
            </div>
          </div>

          {/* メニューアイテム */}
          <div style={{ padding: '10px 0' }}>
            <button
              className={styles['dropdown-item-mobile']}
              onClick={handleAccount}
              data-testid="sidebar__account-link"
            >
              <FaUserCircle className={styles['dropdown-icon']} />
              アカウント設定
            </button>

            {isAdmin && (
              <>
                <button
                  className={styles['dropdown-item-mobile']}
                  onClick={handleUserManagementMobile}
                  data-testid="sidebar__user-management-link"
                >
                  <FaUsers className={styles['dropdown-icon']} />
                  ユーザー管理
                </button>
                <button
                  className={styles['dropdown-item-mobile']}
                  onClick={handleAdminNotificationsMobile}
                  data-testid="sidebar__admin-notifications-link"
                >
                  <FaUsers className={styles['dropdown-icon']} />
                  管理者通知
                </button>
              </>
            )}

            <button
              className={styles['dropdown-item-mobile']}
              onClick={handleLogout}
              data-testid="sidebar__logout-link"
            >
              <FaUserCircle className={styles['dropdown-icon']} />
              ログアウト
            </button>
          </div>
        </div>
      )}

      {!onOpenUploadModal && isModalOpen && (
        <UploadModal onClose={closeModal} onUploadStart={undefined} />
      )}
    </>
  );
};

export default MobileFooter;
